import { PureComponent } from 'react';
import { layer, source, feature } from 'react-openlayers';

import { WHITE } from '../../../rgbColors';
import EventBus from '../../../EventBus';
import { MAP_EVENTS } from '../../../mapEvents';
import { MISSION_EVENTS } from '../../../missionEvents';
import { getCurrentMission } from '../../../dataModelHelpers';
import { MapDebugStorage } from '../../../db/local_storage';
import { SoilDumpEvent } from '../../../db';
import { hideAtZoom, HIDE_TEXT_ZOOM } from './zoomThresholds';

interface DumpEventsLayerProps {
  zoneMission: boolean;
}

interface DumpEventsLayerState {
  features: JSX.Element[];
}

const Z_INDEX_DUMP = 3;

/**
 * DumpEventsLayer — debug-only marker layer that renders soil dump event
 * locations. Only active when MapDebugStorage is on. Step 3 of the
 * KmlLayer split. See docs/superpowers/plans/2026-04-08-kml-layer-split.md.
 *
 * No selection, no interaction. Rebuilds on
 * CORES_NEED_UPDATE (simulator reset clears dumps, bulk ops),
 * MISSION_EVENTS.CREATED_OR_DELETED (mission swap), and
 * MAP_EVENTS.DUMP_SUCCESS (targeted — new dump created during sampling;
 * carries the sample_id so only that sample's dump feature is
 * appended).
 */
export default class DumpEventsLayer extends PureComponent<DumpEventsLayerProps, DumpEventsLayerState> {
  /** Map from sample_id -> JSX features for that sample's dumps. */
  private featuresBySampleId: Map<string, JSX.Element[]> = new Map();

  /** Deterministic sample order used to flatten featuresBySampleId. */
  private sampleOrder: string[] = [];

  constructor(props: DumpEventsLayerProps) {
    super(props);
    this.state = { features: [] };
    this.rebuildAll = this.rebuildAll.bind(this);
    this.onDumpSuccess = this.onDumpSuccess.bind(this);
  }

  componentDidMount() {
    EventBus.on(MAP_EVENTS.CORES_NEED_UPDATE, this.rebuildAll);
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuildAll);
    // SamplingTask.dumpComplete fires this with the sample_id when a
    // new SoilDumpEvent is created. Targeted rebuild — only appends
    // that sample's dump features instead of rebuilding the full list.
    EventBus.on(MAP_EVENTS.DUMP_SUCCESS, this.onDumpSuccess);
    this.rebuildAll();
  }

  componentWillUnmount() {
    EventBus.remove(MAP_EVENTS.CORES_NEED_UPDATE, this.rebuildAll);
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuildAll);
    EventBus.remove(MAP_EVENTS.DUMP_SUCCESS, this.onDumpSuccess);
  }

  // -------------------------------------------------------------------
  // Event handlers
  // -------------------------------------------------------------------

  onDumpSuccess(sampleId?: string) {
    console.log('DumpEventsLayer.onDumpSuccess', sampleId);

    if (!MapDebugStorage.get() || !sampleId) {
      return;
    }

    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    // Rebuild only the dump features for this sample.
    const sampleDumps = mission.getSoilDumpEvents().filter((d) => d.getSample()?.sample_id === sampleId);
    const jsx = this.buildFeaturesForDumps(sampleDumps);
    this.featuresBySampleId.set(sampleId, jsx);

    if (!this.sampleOrder.includes(sampleId)) {
      this.sampleOrder.push(sampleId);
    }

    this.setState({ features: this.flattenFeatures() });
  }

  // -------------------------------------------------------------------
  // Rebuild paths
  // -------------------------------------------------------------------

  rebuildAll() {
    console.count('DumpEventsLayer.rebuildAll');

    if (!MapDebugStorage.get()) {
      this.featuresBySampleId = new Map();
      this.sampleOrder = [];
      this.setState({ features: [] });

      return;
    }

    const mission = getCurrentMission();

    if (!mission) {
      this.featuresBySampleId = new Map();
      this.sampleOrder = [];
      this.setState({ features: [] });

      return;
    }

    const newMap = new Map<string, JSX.Element[]>();
    const order: string[] = [];

    // Group dump events by their parent sample_id.
    const dumpsBySample = new Map<string, SoilDumpEvent[]>();

    for (const dump of mission.getSoilDumpEvents()) {
      const sid = dump.getSample()?.sample_id ?? '__unknown__';

      if (!dumpsBySample.has(sid)) {
        dumpsBySample.set(sid, []);
      }

      dumpsBySample.get(sid)!.push(dump);
    }

    for (const [sid, dumps] of dumpsBySample) {
      newMap.set(sid, this.buildFeaturesForDumps(dumps));
      order.push(sid);
    }

    this.featuresBySampleId = newMap;
    this.sampleOrder = order;
    this.setState({ features: this.flattenFeatures() });
  }

  // -------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------

  private flattenFeatures(): JSX.Element[] {
    const out: JSX.Element[] = [];

    for (const sampleId of this.sampleOrder) {
      const sampleFeatures = this.featuresBySampleId.get(sampleId);

      if (sampleFeatures) {
        out.push(...sampleFeatures);
      }
    }

    return out;
  }

  private buildFeaturesForDumps(dumps: SoilDumpEvent[]): JSX.Element[] {
    const out: JSX.Element[] = [];

    for (const dump of dumps) {
      const kmlFeature = dump.to_feature();

      if (!kmlFeature.geometry.coordinates) {
        continue;
      }

      const dumpText = kmlFeature.properties.name + '\n' + (kmlFeature.properties.core_ids ?? []).join(', ');

      out.push(
        <feature.PointReact
          coordinate={kmlFeature.geometry.coordinates}
          circleOptions={{
            radius: 3,
            strokeOptions: { color: 'pink', width: 5 },
          }}
          key={dumpText}
          zIndex={Z_INDEX_DUMP}
          textOptions={{
            text: dumpText,
            font: '18px Calibri,sans-serif',
            fillOptions: { color: WHITE },
          }}
          hideAtZoom={hideAtZoom(this.props.zoneMission)}
          hideTextZoom={HIDE_TEXT_ZOOM}
          properties={kmlFeature.properties}
        />,
      );
    }

    return out;
  }

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------

  render() {
    return (
      // @ts-ignore - layer.Vector children typing
      <layer.VectorImage zIndex={Z_INDEX_DUMP}>
        {/* @ts-ignore - source.VectorSourceReact children typing */}
        <source.VectorSourceReact>{this.state.features}</source.VectorSourceReact>
      </layer.VectorImage>
    );
  }
}
